/**
 * @author      hugo@zumper.com (Hugo Pineda)
 * @copyright   Copyright (c) 2023, Zumper
 */

/** @jsx jsx */
import { jsx } from '@emotion/react'
import { CarouselV4 } from '@zcode-ui/containers'
import { selectFilters, selectLongTerm } from '@zcode-ui/filters'
import { useMount } from '@zcode-ui/hook'
import { selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import { LIST_CAROUSEL_CLICK } from '@zcode-ui/web-zumper-integrations'
import { useUnloadableMediaIds } from '@zcode-ui/web-zumper-pages'
import { bookingProviders } from '@zcode/nr/es/shared/constants/source'
import type { Image, Listable, ListableImage, Pin, RawPinInventorySearch } from '@zcode/state-type'
import { useCallback, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { openFullDetailTab } from '../../../../../modules/detail/thunks/openFullDetailTab'
import { trackListItemEvent } from '../../../../../modules/traktor/thunks'
import { ImageWrapper } from './ImageWrapper/ImageWrapper'
import { useCarouselExperiment } from './useCarouselExperiment'

const getIdFromImg = (img: HTMLImageElement) => Number(img.dataset.imageid)

export interface CarouselWrapperProps {
  customImageHeight?: number
  customImageWidth?: number
  entity: Listable | Pin<RawPinInventorySearch>
  forceIntersected?: boolean
  hasImage: boolean
  shouldPreloadImage?: boolean
  images: readonly ListableImage[] | null
  isSpotlightVariation?: boolean
  groupId?: number
}

export const CarouselWrapper = ({
  customImageHeight,
  customImageWidth,
  entity,
  forceIntersected,
  hasImage,
  shouldPreloadImage,
  images,
  isSpotlightVariation,
  groupId,
}: CarouselWrapperProps) => {
  const dispatch = useDispatch()
  const currentIndex = useRef(0)
  const [renderAllImages, setRenderAllImages] = useState(false)
  const { addUnloadableMediaId, unloadableMediaIds } = useUnloadableMediaIds({
    getIdFromImg,
    querySelector: 'img[data-imageid]',
  })
  let filtered: readonly ListableImage[] = []
  if (images?.length) {
    filtered = (renderAllImages ? images.slice(0, 4) : images.slice(0, 1)).filter(
      ({ id }) => !unloadableMediaIds.has(id)
    )
  }

  const { maxSlideChanges, activate } = useCarouselExperiment({
    hasEnoughImages: filtered.length >= 4,
  })

  const filters = useSelector(selectFilters)
  const isLongTerm = useSelector(selectLongTerm)
  const isThirdParty =
    !!entity.feedName && bookingProviders.includes(entity.feedName) && !isLongTerm
  const isMobile = useSelector(selectIsMobile)

  const handleSlideChange = useCallback(
    (index: number) => {
      if (index > 1 && !isThirdParty && !isMobile) {
        const variation = activate()
        if (
          variation !== 'A' &&
          index === maxSlideChanges[variation] &&
          index > currentIndex.current
        ) {
          dispatch(
            openFullDetailTab({
              entity,
              filters,
              isGalleryURL: true,
            })
          )
        }
      }
      if (entity && index !== currentIndex.current) {
        const additionalProps = {
          carouselAttributes: {
            action: currentIndex.current < index ? 'right_button_clicked' : 'left_button_clicked',
            index,
            mediaId: entity.imageIds?.[index],
          },
        }
        dispatch(
          trackListItemEvent({
            name: LIST_CAROUSEL_CLICK,
            entity,
            additionalProps,
          })
        )
      }
      currentIndex.current = index
    },
    [dispatch, entity, isThirdParty, isMobile, maxSlideChanges, filtered.length]
  )

  useMount(() => {
    setRenderAllImages(true)
  })

  return hasImage && filtered?.length ? (
    <CarouselV4
      onChangeSlide={handleSlideChange}
      carouselClassName="[&~button]:z-100"
      dots
      dotsCustomClassName={isSpotlightVariation ? 'bottom-[-30px] z-100' : undefined}
      loader
    >
      {filtered.map((image: Image, imagesIndex: number) => (
        <ImageWrapper
          alt={`${entity.headerText ?? 'Listing'} - Photo ${imagesIndex + 1} of ${filtered.length}`}
          customImageHeight={customImageHeight}
          customImageWidth={customImageWidth}
          data-imageid={image.id}
          groupId={groupId}
          key={image.url}
          loading={forceIntersected && imagesIndex === 0 ? 'eager' : 'lazy'}
          onError={addUnloadableMediaId}
          shouldPreload={shouldPreloadImage && imagesIndex === 0}
          src={image.url}
        />
      ))}
    </CarouselV4>
  ) : (
    <ImageWrapper groupId={groupId} />
  )
}
